import { ArrowRight, ChevronLeft, ChevronRight, Lock, Mail, Search, Sparkles, UserPlus } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';

import type { PublicOrg } from '@escala/contracts';
import { LegalLink, useLegal } from '@/components/Privacy';
import { BRAND, BrandMark, pageTitle } from '@/components/Brand';
import OrgMark from '@/components/OrgMark';
import { PasswordInput } from '@/components/PasswordField';
import ThemeToggle from '@/components/ThemeToggle';
import { cn } from '@/design-system/lib/cn';
import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { Input } from '@/design-system/ui/Input';
import { Label } from '@/design-system/ui/Label';
import { INVITE_DAYS } from '@escala/domain/access';
import { orgPlace, orgUrl } from '@escala/domain/orgs';
import { publicApi } from '@/services/api/public';
import { lastOrgSlug, login, requestPasswordReset } from '@/services/api/session';

import { authErrorMessage, problemCode } from './authError';
import InactiveNotice from './components/InactiveNotice';
import GoogleIcon from './components/GoogleIcon';
import Showcase from './components/Showcase';

type Mode = 'org' | 'login' | 'forgot' | 'sent';

const TITLE: Record<Mode, string> = {
  org: 'Encontre sua igreja',
  login: 'Entrar',
  forgot: 'Redefinir senha',
  sent: 'Verifique seu e-mail',
};

/** Ícone à esquerda dentro do campo (e-mail, senha). */
function Leading({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="relative flex items-center">
      <span aria-hidden className="pointer-events-none absolute left-3.5 z-10 flex text-ink-placeholder">
        {icon}
      </span>
      {children}
    </span>
  );
}

/** Espera da busca enquanto se digita. */
const SEARCH_DELAY_MS = 250;

/**
 * "Encontre sua igreja": busca por nome, endereço ou cidade; a última usada aparece
 * marcada e as suspensas, bloqueadas.
 */
function OrgPicker({ onPick }: { onPick: (org: PublicOrg) => void }) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<{ query: string; items: PublicOrg[] } | null>(null);
  const [recent, setRecent] = useState<PublicOrg | null>(null);
  const recentSlug = lastOrgSlug();
  const q = query.trim();
  const searching = q.length >= 2;

  useEffect(() => {
    if (!recentSlug) return;

    let active = true;

    publicApi
      .orgBySlug(recentSlug)
      .then((org) => active && setRecent(org))
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [recentSlug]);

  useEffect(() => {
    if (!searching) return;

    let active = true;
    const timer = setTimeout(() => {
      publicApi
        .searchOrgs(q)
        .then((items) => active && setFound({ query: q, items }))
        .catch(() => active && setFound({ query: q, items: [] }));
    }, SEARCH_DELAY_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [q, searching]);

  const results = found?.query === q ? found.items : [];
  const recentItems = recent ? [recent] : [];
  const items = searching ? results : recentItems;
  const searched = searching && found?.query === q;

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 text-display-lg leading-tight font-extrabold tracking-[-0.03em]">
          Encontre sua igreja
        </h1>
        <p className="m-0 text-body-lg text-ink-subtle">
          Busque pelo nome ou pelo endereço de acesso que você recebeu.
        </p>
      </div>
      <Leading icon={<Search className="size-4 stroke-[1.8]" />}>
        <Input
          aria-label="Buscar organização"
          autoComplete="organization"
          placeholder="Nome da igreja ou livegospel.app/..."
          className="h-12 rounded-block pl-10.5"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </Leading>
      <ul aria-label="Igrejas" className="m-0 flex list-none flex-col gap-2 p-0">
        {items.map((org) => {
          const suspended = org.status === 'suspended';

          return (
            <li key={org.slug}>
              <button
                type="button"
                aria-disabled={suspended}
                onClick={() => !suspended && onPick(org)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-card border border-line-input bg-white px-3.5 py-3 text-left',
                  suspended
                    ? 'cursor-not-allowed opacity-55'
                    : 'hover:border-primary-hover-line hover:bg-primary-faint'
                )}
              >
                <OrgMark org={org} size="lg" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-body-lg font-bold">{org.name}</span>
                    {org.slug === recentSlug ? (
                      <span className="shrink-0 rounded-pill bg-primary-soft px-1.75 py-px text-micro font-semibold text-primary-hover">
                        Último acesso
                      </span>
                    ) : null}
                  </span>
                  <span className="truncate text-meta text-ink-subtle">
                    {suspended
                      ? 'Acesso suspenso — fale com a secretaria'
                      : [orgUrl(org.slug), orgPlace(org)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <ChevronRight aria-hidden className="size-4 shrink-0 text-ink-placeholder" />
              </button>
            </li>
          );
        })}
      </ul>
      {searched && !results.length ? (
        <p className="m-0 rounded-card border-[1.5px] border-dashed border-line-input p-4.5 text-center text-small text-ink-subtle">
          Nenhuma igreja encontrada. Peça o endereço de acesso à secretaria da sua igreja.
        </p>
      ) : null}
      {!searching && !items.length ? (
        <p className="m-0 text-small text-ink-subtle">Digite pelo menos duas letras.</p>
      ) : null}
    </>
  );
}

/**
 * Login em duas etapas: primeiro a igreja ("Encontre sua igreja"), depois e-mail e senha
 * — ou pedir o link para criar uma senha nova. Quem ainda não é membro faz o cadastro
 * pelo link da igreja; quem é membro e não tem acesso recebe o convite do administrador
 * por e-mail. Em telas largas, a metade direita mostra o que o Live Gospel faz.
 */
export default function Login({
  onSignedIn,
  onRegister,
}: {
  onSignedIn: () => void;
  /** Abre o cadastro público da igreja escolhida. */
  onRegister?: (slug: string, token: string) => void;
}) {
  const { openCookies } = useLegal();
  const [mode, setMode] = useState<Mode>('org');
  const [org, setOrg] = useState<PublicOrg | null>(null);
  // Com uma igreja guardada, espera a busca dela para abrir direto no login (sem piscar a busca).
  const [resolving, setResolving] = useState(() => lastOrgSlug() !== null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cadastro inativo: o aviso com "Pedir a exclusão dos meus dados" no lugar do erro.
  const [inactive, setInactive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleNotice, setGoogleNotice] = useState(false);

  useEffect(() => {
    document.title = pageTitle(TITLE[mode]);
  }, [mode]);

  useEffect(() => {
    const slug = lastOrgSlug();

    if (!slug) return;

    let active = true;

    publicApi
      .orgBySlug(slug)
      .then((found) => {
        if (!active || found.status === 'suspended') return;

        setOrg(found);
        setMode('login');
      })
      .catch(() => {})
      .finally(() => active && setResolving(false));

    return () => {
      active = false;
    };
  }, []);

  const go = (next: Mode) => {
    setError(null);
    setInactive(false);
    setGoogleNotice(false);
    setMode(next);
  };

  const submit = (action: () => Promise<unknown>, after: () => void) => async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInactive(false);

    try {
      await action();
      after();
    } catch (err) {
      if (problemCode(err) === 'member_inactive') setInactive(true);
      else setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const emailField = (id: string) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>E-mail</Label>
      <Leading icon={<Mail className="size-4 stroke-[1.8]" />}>
        <Input
          id={id}
          type="email"
          autoComplete="email"
          required
          placeholder="voce@igreja.com.br"
          className="h-11.5 rounded-block pl-10.5"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Leading>
    </div>
  );

  return (
    <main className="grid min-h-dvh grid-cols-1 bg-white text-ink lg:grid-cols-2">
      <section className="flex min-h-dvh min-w-0 flex-col px-4 py-6 sm:px-14 sm:py-10">
        <div className="flex items-center justify-between gap-3">
          <BrandMark />
          <ThemeToggle variant="outline" />
        </div>
        <div className="flex flex-1 items-center justify-center py-10">
          <div className="flex w-full max-w-95 flex-col gap-6">
            {mode === 'org' && !resolving ? (
              <OrgPicker
                onPick={(picked) => {
                  setOrg(picked);
                  go('login');
                }}
              />
            ) : null}

            {mode === 'login' ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <h1 className="m-0 text-display-lg leading-tight font-extrabold tracking-[-0.03em]">
                    Bem-vindo de volta
                  </h1>
                  <p className="m-0 text-body-lg text-ink-subtle">
                    Entre para acompanhar e organizar as escalas do seu ministério.
                  </p>
                </div>
                {org ? (
                  <div className="flex items-center gap-3 rounded-card border border-line-input bg-surface-subtle px-3 py-2.5">
                    <OrgMark org={org} size="lg" />
                    <span className="flex min-w-0 flex-1 flex-col leading-tight">
                      <span className="truncate text-body font-bold">{org.name}</span>
                      <span className="truncate font-mono text-caption text-ink-subtle">
                        {orgUrl(org.slug)}
                      </span>
                    </span>
                    <Button variant="secondary" size="sm" onClick={() => go('org')}>
                      Trocar
                    </Button>
                  </div>
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className="h-11.5 rounded-block font-semibold"
                  onClick={() => setGoogleNotice(true)}
                >
                  <GoogleIcon />
                  Continuar com Google
                </Button>
                {googleNotice ? (
                  <Alert tone="info" role="status">
                    Entrar com Google ainda não está disponível. Use seu e-mail e senha.
                  </Alert>
                ) : null}
                <div className="flex items-center gap-3 text-caption text-ink-placeholder">
                  <span aria-hidden className="h-px flex-1 bg-line-card" />
                  ou com e-mail
                  <span aria-hidden className="h-px flex-1 bg-line-card" />
                </div>
                <form
                  className="flex flex-col gap-4"
                  noValidate
                  onSubmit={submit(() => login(email, password, remember, org?.slug), onSignedIn)}
                >
                  {emailField('login-email')}
                  <div className="flex flex-col gap-1.5">
                    <span className="flex items-center">
                      <Label htmlFor="login-password" className="flex-1">
                        Senha
                      </Label>
                      <Button type="button" variant="link" size="sm" onClick={() => go('forgot')}>
                        Esqueci minha senha
                      </Button>
                    </span>
                    <Leading icon={<Lock className="size-4 stroke-[1.8]" />}>
                      <PasswordInput
                        id="login-password"
                        autoComplete="current-password"
                        required
                        placeholder="Sua senha"
                        className="h-11.5 rounded-block pl-10.5"
                        shown={shown}
                        onToggle={() => setShown((s) => !s)}
                        invalid={!!error}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </Leading>
                  </div>
                  <label className="flex min-h-6 cursor-pointer items-center gap-2.5 text-control text-ink-secondary">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="size-4.5 cursor-pointer accent-primary"
                    />
                    Manter conectado neste dispositivo
                  </label>
                  {error ? <Alert tone="danger">{error}</Alert> : null}
                  {inactive ? <InactiveNotice credentials={{ email, password, org: org?.slug }} /> : null}
                  <Button
                    type="submit"
                    size="lg"
                    className="h-12 rounded-block text-body-lg font-bold shadow-cta"
                    disabled={busy}
                  >
                    {busy ? (
                      <>
                        <span
                          aria-hidden
                          className="size-4 animate-spin rounded-pill border-2 border-current/35 border-t-current"
                        />
                        Entrando…
                      </>
                    ) : (
                      <>
                        Entrar
                        <ArrowRight aria-hidden />
                      </>
                    )}
                  </Button>
                  <p className="m-0 text-center text-meta text-pretty text-ink-subtle">
                    Ao entrar, você concorda com os{' '}
                    <LegalLink doc="terms" orgName={org?.name}>
                      Termos de Uso
                    </LegalLink>{' '}
                    e a{' '}
                    <LegalLink doc="privacy" orgName={org?.name}>
                      Política de Privacidade
                    </LegalLink>
                    .
                  </p>
                </form>
                {org?.registrationToken && onRegister ? (
                  <div className="flex items-center gap-3 rounded-card border border-line-input bg-surface-subtle px-4 py-3.5">
                    <span className="grid size-9 shrink-0 place-items-center rounded-control bg-primary-soft text-primary">
                      <UserPlus aria-hidden className="size-4.5 stroke-[1.8]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="m-0 text-control font-semibold">Ainda não é cadastrado?</p>
                      <p className="m-0 text-meta text-ink-subtle">
                        Preencha seus dados para fazer parte da igreja.
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="border-primary-line text-primary-hover"
                      onClick={() => onRegister(org.slug, org.registrationToken!)}
                    >
                      Fazer cadastro
                    </Button>
                  </div>
                ) : null}
                <Alert className="text-caption [&>svg]:text-primary">
                  <Sparkles aria-hidden />
                  <span className="text-pretty">
                    Já é membro e ainda não tem acesso? Peça um convite ao administrador da sua igreja. O link
                    chega por e-mail e vale por {INVITE_DAYS} dias.
                  </span>
                </Alert>
              </>
            ) : null}

            {mode === 'forgot' || mode === 'sent' ? (
              <Button type="button" variant="link" className="self-start" onClick={() => go('login')}>
                <ChevronLeft aria-hidden />
                Voltar para o login
              </Button>
            ) : null}

            {mode === 'forgot' ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <h1 className="m-0 text-display-lg leading-tight font-extrabold tracking-[-0.03em]">
                    Redefinir senha
                  </h1>
                  <p className="m-0 text-body-lg text-ink-subtle">
                    Informe seu e-mail e enviaremos um link para criar uma nova senha.
                  </p>
                </div>
                <form
                  className="flex flex-col gap-4"
                  noValidate
                  onSubmit={submit(
                    () => requestPasswordReset(email, org?.slug),
                    () => setMode('sent')
                  )}
                >
                  {emailField('forgot-email')}
                  {error ? <Alert tone="danger">{error}</Alert> : null}
                  <Button
                    type="submit"
                    size="lg"
                    className="h-12 rounded-block text-body-lg font-bold shadow-cta"
                    disabled={busy}
                  >
                    Enviar link de redefinição
                  </Button>
                </form>
              </>
            ) : null}

            {mode === 'sent' ? (
              <div role="status" className="flex flex-col items-start gap-3">
                <span className="grid size-13 place-items-center rounded-block bg-success-soft text-success">
                  <Mail aria-hidden className="size-6 stroke-[1.8]" />
                </span>
                <h1 className="m-0 text-display leading-tight font-extrabold tracking-[-0.03em]">
                  Verifique seu e-mail
                </h1>
                <p className="m-0 text-body-lg text-pretty text-ink-secondary">
                  Se houver uma conta para <strong>{email}</strong>, você receberá um link para redefinir a
                  senha em alguns minutos.
                </p>
                <Button variant="secondary" className="mt-1 h-11" onClick={() => go('login')}>
                  Voltar para o login
                </Button>
              </div>
            ) : null}
          </div>
        </div>
        <footer className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-caption text-ink-placeholder">
          <span>
            © {new Date().getFullYear()} {BRAND.full} · {BRAND.tagline}
          </span>
          <nav aria-label="Documentos legais" className="flex flex-wrap gap-x-3">
            <LegalLink doc="privacy" className="font-medium text-ink-subtle">
              Privacidade
            </LegalLink>
            <LegalLink doc="terms" className="font-medium text-ink-subtle">
              Termos
            </LegalLink>
            <button
              type="button"
              onClick={openCookies}
              className="cursor-pointer border-0 bg-transparent p-0 font-medium text-ink-subtle hover:text-primary-hover hover:underline"
            >
              Gerenciar cookies
            </button>
          </nav>
        </footer>
      </section>
      <div className="max-lg:hidden">
        <Showcase />
      </div>
    </main>
  );
}
